/**
 * Calendar Hooks
 * Meeting Prep Feature - React hooks für Kalender-Integration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  calendarApi,
  CalendarEvent,
  RelatedEmail,
  EventWithEmails,
  MeetingPrep,
  groupEventsByDay,
  getEventCountdown,
} from '../api/calendar.api';

// ============ Query Keys ============

export const calendarKeys = {
  all: ['calendar'] as const,
  events: (accountId: string) => [...calendarKeys.all, 'events', accountId] as const,
  upcomingEvents: (accountId: string, days: number) =>
    [...calendarKeys.events(accountId), 'upcoming', days] as const,
  event: (accountId: string, eventId: string) =>
    [...calendarKeys.events(accountId), eventId] as const,
  relatedEmails: (accountId: string, eventId: string) =>
    [...calendarKeys.event(accountId, eventId), 'emails'] as const,
  eventsWithEmails: (accountId: string, days: number) =>
    [...calendarKeys.events(accountId), 'with-emails', days] as const,
  prepNotes: (accountId: string, eventId: string) =>
    [...calendarKeys.event(accountId, eventId), 'notes'] as const,
};

// ============ Hooks ============

/**
 * Hook to fetch upcoming calendar events
 * @param accountId - Mail account ID
 * @param days - Number of days to look ahead (default: 7)
 */
export function useUpcomingEvents(accountId: string | undefined, days: number = 7) {
  return useQuery({
    queryKey: calendarKeys.upcomingEvents(accountId || '', days),
    queryFn: () => calendarApi.getUpcomingEvents(accountId!, days),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
  });
}

/**
 * Hook to fetch a single calendar event
 * @param accountId - Mail account ID
 * @param eventId - Calendar event ID
 */
export function useCalendarEvent(accountId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: calendarKeys.event(accountId || '', eventId || ''),
    queryFn: () => calendarApi.getEvent(accountId!, eventId!),
    enabled: !!accountId && !!eventId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch emails related to a calendar event
 * @param accountId - Mail account ID
 * @param eventId - Calendar event ID
 */
export function useEventEmails(accountId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: calendarKeys.relatedEmails(accountId || '', eventId || ''),
    queryFn: () => calendarApi.getRelatedEmails(accountId!, eventId!),
    enabled: !!accountId && !!eventId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch all upcoming events with their related emails
 * Combined view for Meeting Prep column
 * @param accountId - Mail account ID
 * @param days - Number of days to look ahead
 */
export function useEventsWithEmails(accountId: string | undefined, days: number = 7) {
  return useQuery({
    queryKey: calendarKeys.eventsWithEmails(accountId || '', days),
    queryFn: () => calendarApi.getEventsWithEmails(accountId!, days),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for Meeting Preps - combines events with related emails
 * and provides additional computed properties
 * @param accountId - Mail account ID
 * @param days - Number of days to look ahead
 */
export function useMeetingPreps(accountId: string | undefined, days: number = 7) {
  const { data, isLoading, error, refetch } = useEventsWithEmails(accountId, days);

  // Transform to MeetingPrep format with additional computed data
  const meetingPreps = useMemo(() => {
    if (!data?.events) return [];

    return data.events
      .filter((e) => !e.event.isCancelled)
      .map((eventWithEmails): MeetingPrep & { countdown: ReturnType<typeof getEventCountdown> } => ({
        event: eventWithEmails.event,
        relatedEmails: eventWithEmails.emails,
        countdown: getEventCountdown(eventWithEmails.event.start),
      }))
      .sort((a, b) => {
        // Sort by urgency first, then by time
        const urgencyOrder = { now: 0, soon: 1, today: 2, tomorrow: 3, thisWeek: 4, later: 5 };
        const urgencyDiff = urgencyOrder[a.countdown.urgency] - urgencyOrder[b.countdown.urgency];
        if (urgencyDiff !== 0) return urgencyDiff;
        return a.countdown.minutesUntil - b.countdown.minutesUntil;
      });
  }, [data]);

  // Group by day for calendar view
  const prepsByDay = useMemo(() => {
    const events = meetingPreps.map((p) => p.event);
    return groupEventsByDay(events);
  }, [meetingPreps]);

  // Stats
  const stats = useMemo(() => {
    const soonCount = meetingPreps.filter(
      (p) => p.countdown.urgency === 'soon' || p.countdown.urgency === 'now'
    ).length;
    const todayCount = meetingPreps.filter((p) => p.countdown.urgency === 'today').length;
    const totalEmails = meetingPreps.reduce((sum, p) => sum + p.relatedEmails.length, 0);

    return {
      total: meetingPreps.length,
      soonCount,
      todayCount,
      totalEmails,
      hasUrgent: soonCount > 0,
    };
  }, [meetingPreps]);

  return {
    meetingPreps,
    prepsByDay,
    stats,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to fetch meeting prep notes
 * @param accountId - Mail account ID
 * @param eventId - Calendar event ID
 */
export function usePrepNotes(accountId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: calendarKeys.prepNotes(accountId || '', eventId || ''),
    queryFn: () => calendarApi.getPrepNotes(accountId!, eventId!),
    enabled: !!accountId && !!eventId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============ Mutations ============

/**
 * Hook to save meeting prep notes
 */
export function useSavePrepNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      eventId,
      notes,
    }: {
      accountId: string;
      eventId: string;
      notes: string;
    }) => calendarApi.savePrepNotes(accountId, eventId, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.prepNotes(variables.accountId, variables.eventId),
      });
    },
  });
}

/**
 * Hook to manually link an email to an event
 */
export function useLinkEmailToEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      eventId,
      messageId,
    }: {
      accountId: string;
      eventId: string;
      messageId: string;
    }) => calendarApi.linkEmailToEvent(accountId, eventId, messageId),
    onSuccess: (_, variables) => {
      // Invalidate related emails for this event
      queryClient.invalidateQueries({
        queryKey: calendarKeys.relatedEmails(variables.accountId, variables.eventId),
      });
      // Invalidate combined view
      queryClient.invalidateQueries({
        queryKey: calendarKeys.eventsWithEmails(variables.accountId, 7),
      });
    },
  });
}

/**
 * Hook to unlink an email from an event
 */
export function useUnlinkEmailFromEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      eventId,
      messageId,
    }: {
      accountId: string;
      eventId: string;
      messageId: string;
    }) => calendarApi.unlinkEmailFromEvent(accountId, eventId, messageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.relatedEmails(variables.accountId, variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.eventsWithEmails(variables.accountId, 7),
      });
    },
  });
}

/**
 * Hook to sync calendar from provider
 */
export function useSyncCalendar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => calendarApi.syncCalendar(accountId),
    onSuccess: (_, accountId) => {
      // Invalidate all calendar queries for this account
      queryClient.invalidateQueries({
        queryKey: calendarKeys.events(accountId),
      });
    },
  });
}

// ============ Utility Hooks ============

/**
 * Hook to get the next upcoming event
 * Useful for showing in header/sidebar
 */
export function useNextEvent(accountId: string | undefined) {
  const { data, isLoading } = useUpcomingEvents(accountId, 1);

  const nextEvent = useMemo(() => {
    if (!data?.events || data.events.length === 0) return null;

    const now = new Date();
    const upcoming = data.events
      .filter((e) => new Date(e.start) > now && !e.isCancelled)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    return upcoming[0] || null;
  }, [data]);

  const countdown = useMemo(() => {
    if (!nextEvent) return null;
    return getEventCountdown(nextEvent.start);
  }, [nextEvent]);

  return {
    event: nextEvent,
    countdown,
    isLoading,
  };
}

/**
 * Hook to check if there are any events happening soon
 * Returns true if any event is within the threshold
 */
export function useHasUpcomingUrgentEvents(
  accountId: string | undefined,
  thresholdMinutes: number = 30
) {
  const { data, isLoading } = useUpcomingEvents(accountId, 1);

  const hasUrgent = useMemo(() => {
    if (!data?.events) return false;

    const now = new Date();
    const thresholdMs = thresholdMinutes * 60 * 1000;

    return data.events.some((e) => {
      const start = new Date(e.start);
      const diff = start.getTime() - now.getTime();
      return diff > 0 && diff <= thresholdMs && !e.isCancelled;
    });
  }, [data, thresholdMinutes]);

  return { hasUrgent, isLoading };
}
